<script setup>
import { ref, watch } from 'vue';
import { t } from '../i18n';

// Payout goal of one portfolio: per month or per year, RUB after tax (docs/payouts/SPEC.md, decision 12).
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  // { period, amount } or null
  goal: {
    type: Object,
    default: null,
  },
  portfolioName: {
    type: String,
    default: '',
  },
  saving: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'save', 'remove']);

const period = ref('month');
const amount = ref('');
const localError = ref('');

function submit() {
  localError.value = '';
  const value = Number(String(amount.value).replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(value) || value <= 0) {
    localError.value = t('goal.invalidAmount');
    return;
  }
  emit('save', { period: period.value, amount: value });
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      period.value = props.goal?.period ?? 'month';
      amount.value = props.goal ? String(Number(props.goal.amount)) : '';
      localError.value = '';
    }
  },
  { immediate: true }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 sm:items-center sm:p-4"
      @click.self="emit('close')"
    >
      <form
        class="w-full rounded-t-3xl sm:max-w-sm bg-slate-900 p-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] shadow-2xl max-sm:max-h-[88dvh] max-sm:overflow-y-auto sm:rounded-xl sm:border sm:border-slate-700 sm:pb-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payout-goal-title"
        @submit.prevent="submit"
      >
        <h3
          id="payout-goal-title"
          class="text-base font-semibold text-slate-100"
        >
          {{ t('goal.title') }}
        </h3>
        <p class="mt-1 text-xs text-slate-500">
          {{ portfolioName }} · {{ t('goal.hint') }}
        </p>

        <div
          class="mt-4 inline-flex rounded-lg border border-slate-700 p-0.5 text-sm"
          role="radiogroup"
          :aria-label="t('goal.period')"
        >
          <button
            v-for="option in ['month', 'year']"
            :key="option"
            type="button"
            role="radio"
            :aria-checked="period === option"
            class="rounded-md px-4 py-1.5"
            :class="period === option ? 'bg-slate-700 text-slate-100' : 'text-slate-400 hover:text-slate-200'"
            @click="period = option"
          >
            {{ t(`goal.per.${option}`) }}
          </button>
        </div>

        <label class="mt-4 block text-xs text-slate-400">
          {{ t('goal.amount') }}
          <div class="mt-1 flex items-center gap-2">
            <input
              v-model="amount"
              type="text"
              inputmode="decimal"
              class="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              placeholder="10 000"
            >
            <span class="text-sm text-slate-400">₽</span>
          </div>
        </label>

        <p
          v-if="localError || error"
          class="mt-3 text-sm text-rose-400"
        >
          {{ localError || error }}
        </p>

        <div class="mt-6 flex items-center justify-between gap-2">
          <button
            v-if="goal"
            type="button"
            class="text-sm text-rose-300 hover:text-rose-200 disabled:opacity-50"
            :disabled="saving"
            @click="emit('remove')"
          >
            {{ t('goal.remove') }}
          </button>
          <span v-else />
          <div class="flex gap-2">
            <button
              type="button"
              class="rounded bg-slate-800 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700"
              @click="emit('close')"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="rounded bg-emerald-600 px-4 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
              :disabled="saving"
            >
              {{ saving ? t('common.saving') : t('goal.save') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </Teleport>
</template>
